import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  Brain,
  Heart,
  Smartphone,
  GraduationCap,
  Book,
  ArrowRight,
  CheckCircle,
  X,
  Zap,
  TrendingUp,
  AlertTriangle,
  BookOpen,
  Users,
  Monitor,
  Activity
} from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const WhyPage: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Why RSVP? Speed Reading Science & Benefits | Flash-Read',
    description: 'Learn the science behind RSVP speed reading. Peer-reviewed studies show 286% speed increases, 72% peripheral vision gains. Discover RSVP benefits for every reader.',
  });

  const [isVisible, setIsVisible] = useState(false);
  const [countersAnimated, setCountersAnimated] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    // Animate counters after a delay
    const timer = setTimeout(() => setCountersAnimated(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const stats = [
    { 
      icon: Zap, 
      value: '286%', 
      label: 'Maximum Speed Increase',
      color: 'amber',
      description: 'Rubin & Turano (1992)' 
    },
    { 
      icon: TrendingUp, 
      value: '72%', 
      label: 'Peripheral Vision Gain',
      color: 'violet',
      description: 'Yu et al. (2010)' 
    },
    {
      icon: Eye,
      value: '53%',
      label: 'Vision Improvement',
      color: 'emerald',
      description: 'RSVP training for low-vision readers (Chung et al.)'
    },
    { 
      icon: Brain, 
      value: '1,652', 
      label: 'WPM Peak Rate',
      color: 'blue',
      description: 'Rubin & Turano (1992)' 
    },
  ];

  const pillars = [
    {
      icon: Eye,
      title: 'Optimal Recognition Point (ORP)',
      description: 'The red letter in each word is your brain\'s fastest recognition point. Your eyes lock onto one position — no scanning, no wasted movement. Flash-Read does this automatically.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Activity,
      title: 'Eliminate rapid eye movements (saccadic)',
      description: 'No time lost jumping between words; your brain processes text continuously without interruption.',
      gradient: 'from-violet-500 to-purple-500'
    },
    {
      icon: Zap,
      title: 'Rhythmic Flow',
      description: 'Consistent timing helps your brain enter a flow state, making reading feel effortless.',
      gradient: 'from-amber-500 to-orange-500'
    },
  ];

  const userBenefits = [
    {
      icon: BookOpen,
      title: 'Casual Readers',
      description: 'Consume more books, articles, and content in less time.',
      color: 'blue'
    },
    {
      icon: GraduationCap,
      title: 'Students',
      description: 'Speed through textbooks and research papers efficiently.',
      color: 'violet'
    },
    {
      icon: Monitor,
      title: 'Professionals',
      description: 'Stay on top of reports, industry news, and emails.',
      color: 'emerald'
    },
    {
      icon: Eye,
      title: 'Vision Impaired',
      description: '53% reading speed improvement for macular disease patients.',
      color: 'amber'
    },
    {
      icon: Users,
      title: 'Language Learners',
      description: 'Improve fluency and comprehension simultaneously.',
      color: 'pink'
    },
  ];

  const comparisonData = [
    { aspect: 'Eye Movement', traditional: 'Constant saccades', rsvp: 'None - fixed position', rsvpWins: true },
    { aspect: 'Speed', traditional: '200-300 WPM avg', rsvp: '300-1,000+ WPM', rsvpWins: true },
    { aspect: 'Space Required', traditional: 'Full page/screen', rsvp: 'Single word display', rsvpWins: true },
    { aspect: 'Multitasking', traditional: 'Difficult', rsvp: 'Easier', rsvpWins: true },
    { aspect: 'Small Screens', traditional: 'Cumbersome', rsvp: 'Optimal performance', rsvpWins: true },
  ];

  const citations = [
    'Rubin, G. S., & Turano, K. (1992). Reading without saccadic eye movements. Vision Research, 32(5), 895–902.',
    'Yu, D., Cheung, S. H., Legge, G. E., & Chung, S. T. (2010). Reading speed in the peripheral visual field of older adults: Does it benefit from perceptual learning? Vision Research, 50(9), 860–869.',
    'Benedetto, S., Carbone, A., Pedrotti, M., Le Fevre, K., Bey, L. A. T., & Baccino, T. (2015). Rapid serial visual presentation in reading: The case of Spritz. Computers in Human Behavior, 45, 352–358.',
    'Hedin, A., & Lindgren, T. (2007). Reading with RSVP on a mobile phone.',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* 1. Hero Section */}
      <section className={`relative pt-32 pb-4 px-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            Why RSVP?{' '}
            <span className="block">
              <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">The Science</span>{' '}
              of Speed Reading
            </span>
          </h1>
        {/* Keyword-rich intro paragraph */}
        <div className="stories-intro mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-3xl mx-auto">
          <p className="text-base md:text-lg text-slate-700 dark:text-slate-300 leading-relaxed text-center">
            <span className="keyword-highlight">Why RSVP?</span> The <span className="keyword-highlight">speed reading science</span> behind faster comprehension.<br />
            <span className="keyword-highlight">Peer-reviewed research</span> shows 286% speed increases with RSVP.<br />
            <span className="keyword-highlight">Eliminate eye movements</span> and read 3x faster.<br />
            Discover the <span className="keyword-highlight">RSVP benefits</span> for every type of reader.
          </p>
        </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-medium mb-2">
            <Zap className="w-4 h-4" />
            <span>Scientifically Proven Method</span>
          </div>
        </div>
      </section>

      {/* 2. Speed Benefits Section */}
      <section className={`py-8 md:py-10 px-4 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Read 3x Faster
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              The numbers speak for themselves. RSVP has been proven to dramatically increase reading speed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="group p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`w-12 h-12 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <stat.icon className={`h-6 w-6 text-${stat.color}-500`} />
                </div>
                <div className="text-4xl font-bold text-slate-900 dark:text-white mb-1 font-mono">
                  {countersAnimated ? stat.value : '0%'}
                </div>
                <div className="text-sm text-slate-500 uppercase tracking-wide mb-2">
                  {stat.label}
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. How It Works Section */}
      <section className={`py-16 md:py-20 px-4 bg-white dark:bg-slate-900/50 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              How RSVP Works
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Three pillars that make RSVP the most efficient reading method
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pillars.map((pillar, index) => (
              <div 
                key={index}
                className="relative group text-center"
              >
                {/* Connector line for desktop */}
                {index < pillars.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-slate-200 to-slate-200 dark:from-slate-700 dark:to-slate-700 -translate-y-1/2 translate-x-8" />
                )}
                
                <div className="relative inline-flex flex-col items-center">
                  <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${pillar.gradient} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <pillar.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORP Visual Comparison */}
      <section className={`py-16 md:py-20 px-4 transition-all duration-1000 delay-350 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-medium mb-4">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Optimized for Flash-Read
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              The Optical Recognition Point Advantage
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              See why the red letter makes all the difference
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Traditional Reading */}
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <X className="w-5 h-5 text-red-500" />
                Traditional Reading
              </h3>
              <div className="text-sm text-slate-600 dark:text-slate-400 space-y-3">
                <p>Your eyes jump around each word:</p>
                <div className="font-mono text-sm bg-slate-100 dark:bg-slate-700 p-4 rounded-lg">
                  <span className="text-slate-700 dark:text-slate-300">The quick brown fox jumps</span>
                </div>
                <p className="text-red-500 font-medium">~30% of reading time lost to eye movement</p>
              </div>
            </div>

            {/* RSVP with ORP */}
            <div className="p-6 rounded-2xl bg-white dark:bg-slate-800 border border-emerald-200 dark:border-emerald-700">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                RSVP with ORP
              </h3>
              <div className="text-sm text-center text-slate-600 dark:text-slate-400 space-y-3">
                <p>Your eyes stay fixed on one point:</p>
                <div className="space-y-2">
                  <div className="font-mono text-2xl text-center py-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    t<span className="text-red-500 font-bold">h</span>e
                  </div>
                  <div className="font-mono text-2xl text-center py-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    qu<span className="text-red-500 font-bold">i</span>ck
                  </div>
                  <div className="font-mono text-2xl text-center py-2 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    br<span className="text-red-500 font-bold">o</span>wn
                  </div>
                </div>
                <p className="text-emerald-500 font-medium">Zero eye movement. Maximum efficiency.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. User Benefits Section */}
      <section className={`py-16 md:py-20 px-4 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Benefits for Every Reader
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Whether you're a casual reader or a professional saving time, RSVP has something for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {userBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
              >
                <div className={`w-12 h-12 bg-${benefit.color}-100 dark:bg-${benefit.color}-900/30 rounded-xl flex items-center justify-center mb-4`}>
                  <benefit.icon className={`h-6 w-6 text-${benefit.color}-500`} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Comparison Section */}
      <section className={`py-8 px-4 bg-white dark:bg-slate-900/50 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-5xl mx-auto text-center mb-12">
          <button
            onClick={() => navigate('/signup')}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all transform hover:scale-105"
          >
            View Pricing Plans
          </button>
        </div>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              RSVP vs. Traditional Reading
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              See why thousands of readers are making the switch
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-xl">
            <div className="grid grid-cols-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold p-4">
              <div className="text-left">Aspect</div>
              <div className="text-center">Traditional</div>
              <div className="text-center">RSVP</div>
            </div>
            {comparisonData.map((row, index) => (
              <div 
                key={index}
                className={`grid grid-cols-3 p-4 ${index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'} border-t border-slate-100 dark:border-slate-700`}
              >
                <div className="text-slate-900 dark:text-white font-medium">{row.aspect}</div>
                <div className="text-center text-slate-500 dark:text-slate-400">{row.traditional}</div>
                <div className="text-center">
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                    {row.rsvpWins && <CheckCircle className="w-4 h-4" />}
                    {row.rsvp}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehension Warning Box */}
      <section className={`py-8 px-4 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-amber-500 mr-4 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-amber-800 dark:text-amber-200 mb-2">
                  Speed vs. Comprehension
                </h4>
                <p className="text-amber-700 dark:text-amber-300">
                  Reading beyond your comfortable speed may reduce comprehension. 
                  Start slow and gradually increase WPM as your skills improve.
                  Studies show comprehension is maintained up to ~350 WPM.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Research Citations Section */}
      <section className={`py-8 px-4 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Backed by Science
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Research citations from peer-reviewed studies
            </p>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 border border-slate-200 dark:border-slate-700 shadow-sm">
            <ul className="space-y-4">
              {citations.map((citation, index) => (
                <li key={index} className="flex items-start">
                  <Book className="w-5 h-5 text-violet-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400 italic">
                    {citation}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 text-center">
              <button
                onClick={() => navigate('/research')}
                className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline font-medium"
              >
                Explore the speed reading research →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Call to Action Section */}
      <section className={`py-4 px-4 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="max-w-4xl mx-auto text-center mb-12">
          <button
            onClick={() => navigate('/signup#pricing')}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transition-all transform hover:scale-105"
          >
            Get Started with Reader Tier
          </button>
        </div>
      </section>
    </div>
  );
};

export default WhyPage;
